import type { EmailAccount } from "~/react/components/email_accounts/email_accounts_filters"

// Import provider icons to ensure proper asset resolution in production
import gmailIcon from "~/images/connected_apps/gmail_icon.svg"
import outlookIcon from "~/images/connected_apps/outlook_icon.svg"
import smtpImapIcon from "~/images/connected_apps/smtp_imap_icon.svg"

export { smtpImapIcon }

// Map of provider icons using imported assets for proper production builds
export const providerIconMap: Record<string, string> = {
  gmail: gmailIcon,
  outlook: outlookIcon,
  smtp_imap: smtpImapIcon,
}

export type CapacityData = {
  sent: number
  limit: number
  scheduled: number
}

export type ColumnId = "email" | "sender_name" | "status" | "capacity" | "scheduled" | "health" | "owner" | "actions"

export type ColumnDef = {
  id: ColumnId
  sticky?: boolean
  renderHeader: () => React.ReactNode
  renderCell: (account: EmailAccount) => React.ReactNode
  cellProps?: React.TdHTMLAttributes<HTMLTableCellElement>
}

export const getInitials = (name: string): string => {
  if (!name) return ""
  const parts = name.trim().split(/\s+/)
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase()
  return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase()
}

// Helper to extract health score, handling both number and object formats
export const getHealthScore = (
  health: number | null | { score: number; status?: string } | undefined,
): number | null => {
  if (typeof health === "number") return health
  if (health === null || health === undefined) return null
  if (typeof health === "object" && "score" in health) {
    return health.score
  }
  return null
}

export const STORAGE_KEY = "email_accounts_center_email_accounts.columns"

/** Pending accounts have no EmailAccount record yet. Raw data has id null; transformed data uses string ids like "pending:email@example.com". Do not rely on status from the serializer. */
export function isPendingAccount(account: { id?: number | string | null; status?: string }): boolean {
  return account.id == null || typeof account.id === "string"
}

/** Resolve the status of an account from async statusData, falling back to account.status */
export function resolveAccountStatus(
  account: { id?: number | string | null; status?: string },
  statusData: Record<string, { status: string }>,
): string | undefined {
  if (isPendingAccount(account)) return account.status
  return statusData[String(account.id)]?.status ?? account.status
}
